<script setup>
import { ref, computed } from 'vue'
import ChartView from '@/components/charts/ChartView.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'

const bp = computed(() => store.salesExecV2.businessPotential.value)
const rows = computed(() => [...bp.value.rows].sort((a,b) => b.opportunityGap - a.opportunityGap))
const segChart = computed(() => ({
  labels: Object.keys(bp.value.segmentation),
  datasets: [{ data: Object.values(bp.value.segmentation), backgroundColor: ['#5B4E9E','#C99A2E','#8B959B','#92583A'] }]
}))

const drawerCustomerId = ref(null)
const drawerRow = computed(() => drawerCustomerId.value ? rows.value.find(r => r.customerId === drawerCustomerId.value) || null : null)
function openDrawer(r) { drawerCustomerId.value = r.customerId }
function growthLabel(pct) {
  if (pct === null) return { text: 'New', cls: '' }
  if (pct > 0) return { text: `+${pct}%`, cls: 'color:var(--status-green);font-weight:700;' }
  if (pct < 0) return { text: `${pct}%`, cls: 'color:var(--status-red);font-weight:700;' }
  return { text: '0%', cls: 'color:var(--color-text-muted);' }
}
</script>

<template>
  <div>
    <div class="card module-header"><h2>Business Potential</h2></div>

    <div class="split-side-main" style="margin-bottom:16px;">
      <div class="card" style="padding:18px;"><div class="eyebrow" style="margin-bottom:8px;">Segmentation</div><ChartView type="doughnut" :labels="segChart.labels" :datasets="segChart.datasets" :height="200" key="se-bp-seg" /></div>
      <div class="stat-row" style="margin-bottom:0;">
        <div class="card stat-card"><div class="stat-value" style="color:#5B4E9E;">{{ bp.segmentation.Platinum }}</div><div class="stat-label">Platinum</div></div>
        <div class="card stat-card"><div class="stat-value" style="color:#97731f;">{{ bp.segmentation.Gold }}</div><div class="stat-label">Gold</div></div>
        <div class="card stat-card"><div class="stat-value" style="color:#5B6469;">{{ bp.segmentation.Silver }}</div><div class="stat-label">Silver</div></div>
        <div class="card stat-card"><div class="stat-value" style="color:#92583A;">{{ bp.segmentation.Bronze }}</div><div class="stat-label">Bronze</div></div>
      </div>
    </div>

    <div class="card">
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Pharmacy</th><th>Category</th><th>Score</th><th>Class.</th><th>Orders</th><th>Avg Order</th><th>Growth %</th><th>Product Mix</th><th>Actual</th><th>Potential</th><th>Opportunity Gap</th></tr></thead>
          <tbody>
            <tr v-for="p in rows" :key="p.customerId" class="clickable" @click="openDrawer(p)">
              <td class="cell-strong">{{ p.pharmacyName }}</td><td class="cell-muted">{{ p.category }}</td>
              <td class="cell-muted">{{ p.potentialScore }}</td>
              <td><span class="tier-badge" :class="p.potentialClassification">{{ p.potentialClassification }}</span></td>
              <td class="cell-muted">{{ p.orderCount }}</td>
              <td class="cell-muted">{{ p.avgOrderValue ? '$'+p.avgOrderValue.toLocaleString() : '—' }}</td>
              <td :style="growthLabel(p.growthPct).cls">{{ growthLabel(p.growthPct).text }}</td>
              <td class="cell-muted">
                <template v-if="p.divisionCount">{{ p.topProduct }} {{ p.topProductPct }}%</template>
                <template v-else>—</template>
              </td>
              <td class="cell-muted">${{ p.actual.toLocaleString() }}</td><td class="cell-muted">${{ p.potentialValue.toLocaleString() }}</td>
              <td :style="p.opportunityGap>0?'color:var(--color-accent);font-weight:600;':'color:var(--status-green);'">{{ p.opportunityGap>0?'$'+p.opportunityGap.toLocaleString():'Realized' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="!!drawerRow" :title="drawerRow?.pharmacyName || ''" @close="drawerCustomerId=null">
      <template v-if="drawerRow">
        <div class="stat-row" style="margin-bottom:16px;">
          <div class="card stat-card" style="box-shadow:none;"><div class="stat-value">${{ drawerRow.monthlyValue.toLocaleString() }}</div><div class="stat-label">Monthly</div></div>
          <div class="card stat-card" style="box-shadow:none;"><div class="stat-value">${{ drawerRow.quarterlyValue.toLocaleString() }}</div><div class="stat-label">Quarterly</div></div>
          <div class="card stat-card" style="box-shadow:none;"><div class="stat-value">${{ drawerRow.annualValue.toLocaleString() }}</div><div class="stat-label">Annual</div></div>
        </div>
        <dl style="display:grid;grid-template-columns:auto 1fr;gap:8px 16px;margin:0;font-size:13px;">
          <dt class="cell-muted">Potential Score</dt><dd style="margin:0">{{ drawerRow.potentialScore }} / 100</dd>
          <dt class="cell-muted">Classification</dt><dd style="margin:0"><span class="tier-badge" :class="drawerRow.potentialClassification">{{ drawerRow.potentialClassification }}</span></dd>
          <dt class="cell-muted">Number of Orders</dt><dd style="margin:0">{{ drawerRow.orderCount }}</dd>
          <dt class="cell-muted">Average Order Value</dt><dd style="margin:0">{{ drawerRow.avgOrderValue ? '$'+drawerRow.avgOrderValue.toLocaleString() : '—' }}</dd>
          <dt class="cell-muted">Growth %</dt><dd style="margin:0" :style="growthLabel(drawerRow.growthPct).cls">{{ growthLabel(drawerRow.growthPct).text }}</dd>
          <dt class="cell-muted">Product Mix</dt><dd style="margin:0">{{ drawerRow.divisionCount ? drawerRow.topProduct+' '+drawerRow.topProductPct+'%' : '—' }}</dd>
          <dt class="cell-muted">Potential vs Actual</dt><dd style="margin:0">${{ drawerRow.actual.toLocaleString() }} / ${{ drawerRow.potentialValue.toLocaleString() }}</dd>
          <dt class="cell-muted">Opportunity Gap</dt><dd style="margin:0" :style="drawerRow.opportunityGap>0?'color:var(--color-accent);font-weight:600;':'color:var(--status-green);'">{{ drawerRow.opportunityGap>0?'$'+drawerRow.opportunityGap.toLocaleString():'Realized' }}</dd>
        </dl>
      </template>
    </DetailDrawer>
  </div>
</template>
